import { Fragment, useState, type ReactNode } from "react";
import {
  BadgeCheck,
  Building2,
  ExternalLink,
  Globe,
  Lock,
  MessageSquare,
  ShieldCheck,
  Star,
  ThumbsUp,
  XIcon,
} from "@semo/icons";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Skeleton } from "../../primitives/skeleton";
import { cn } from "../../utils/cn";
import { VendorAvatar } from "../vendor/VendorAvatar";
import {
  LastActiveBadge,
  ProfileHero,
  RatingStars,
  formatTrustPercentile,
  formatYearMonth,
  getCertExpiryState,
  toAvatarName,
  toDisplayHost,
} from "./profile-parts";

/**
 * 기업 프로필 데이터 — 백엔드 SupplierPublicProfileDto 와 1:1.
 * (api-contracts 재생성 전에도 쓸 수 있도록 여기서 타입을 정의한다)
 */
export interface CompanyProfileData {
  supplierId: string;
  isRevealed: boolean;
  companyName: string | null;
  ceoName: string | null;
  region: string | null;
  homepageUrl: string | null;
  logoUrl: string | null;
  landingThumbnailUrl: string | null;
  companyIntro: string | null;
  productDescription: string | null;
  establishedAt: string | null;
  yearsInBusiness: number | null;
  joinedAt: string | null;
  trustScore: number | null;
  trustPercentile: number | null;
  reviewSummary: {
    totalReviews: number;
    averageRating: number;
    positiveReviewCount: number;
    negativeReviewCount: number;
    topTags: { label: string; count: number }[];
  };
  certificationCount: number;
  certifications: {
    id: string;
    name: string;
    issuer: string | null;
    certNumber: string | null;
    issuedAt: string | null;
    expiresAt: string | null;
    status: string;
    /** SELF: 세모 직접 업로드 · CMARKET: 씨마켓 회원프로필 동기화(세모에서 삭제 불가) */
    source?: string;
    kind?: string | null;
    rejectionReason: string | null;
    fileUrl: string | null;
    fileName: string | null;
    createdAt: string;
  }[];
  /**
   * 공공기관 의무구매 대상 인증 분야 — 씨마켓이 승인·유효기간까지 판정해 동기화로 넘겨준다.
   * I·O1~O3·L·R·S·K 는 회사 단위, P0(기술개발제품)·Q0(녹색제품)은 제품 단위 인증이라
   * «사면 실적 인정» 이 아니라 «대상 인증 보유» 로 표기한다.
   */
  procurementCategories?: {
    code: string;
    label: string;
    /** 유효 인증 기준 만료일 — 지난 분야는 화면에서 숨긴다 */
    validUntil: string | null;
  }[];
  /** 최근 N년 거래 실적 — 기관 이름은 익명 단계에서 비운다 */
  recentTransactions?: {
    years: number;
    contractCount: number;
    institutionCount: number;
    /** 거래 많은 순 상위 기관 */
    topInstitutions: { name: string; contractCount: number }[];
  } | null;
  /** 최근 접속 시각 */
  lastActiveAt?: string | null;
}

/** 견적 카드 등에 붙는 한 줄 요약 — 프로필 전체에서 뽑아 쓴다 */
export interface CompanyProfileSummary {
  averageRating: number;
  totalReviews: number;
  yearsInBusiness: number | null;
  certificationCount: number;
  logoUrl: string | null;
}

export function toProfileSummary(
  profile: CompanyProfileData,
): CompanyProfileSummary {
  return {
    averageRating: profile.reviewSummary.averageRating,
    totalReviews: profile.reviewSummary.totalReviews,
    yearsInBusiness: profile.yearsInBusiness,
    certificationCount: profile.certificationCount,
    logoUrl: profile.logoUrl,
  };
}

type Certification = CompanyProfileData["certifications"][number];

/** "실험 소모품 · 분석 시약, 초자류" → 칩 목록 */
function splitProductDescription(description: string | null): string[] {
  if (!description) return [];
  return description
    .split(/[·,、/\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 12);
}

function Section({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

function StatTile({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-xl bg-slate-50 px-3 py-2.5">
      <span className="text-2xs font-medium text-slate-500">{label}</span>
      <span className="inline-flex items-center gap-1 truncate text-base font-bold tabular-nums text-slate-900">
        {value}
      </span>
      {sub && (
        <span
          className={cn(
            "truncate text-2xs",
            accent ? "font-semibold text-primary-text" : "text-slate-400",
          )}
        >
          {sub}
        </span>
      )}
    </div>
  );
}

function EmptyNote({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-dashed border-slate-200 px-3.5 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-700">{title}</p>
        <p className="text-2xs text-slate-500">{description}</p>
      </div>
    </div>
  );
}

function CertificationRow({ cert }: { cert: Certification }) {
  const expiry = getCertExpiryState(cert.expiresAt);
  const expiresLabel = formatYearMonth(cert.expiresAt);
  const isInactive = expiry === "expired" || cert.status === "REJECTED";

  const statusChip =
    cert.status === "PENDING"
      ? { label: "검증 대기", className: "bg-slate-100 text-slate-500" }
      : cert.status === "REJECTED"
        ? { label: "반려", className: "bg-rose-50 text-rose-600" }
        : expiry === "expired"
          ? { label: "만료", className: "bg-rose-50 text-rose-600" }
          : expiry === "soon"
            ? { label: "만료 임박", className: "bg-amber-50 text-amber-700" }
            : null;

  return (
    <li className="flex items-center gap-3 px-3.5 py-2.5">
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
          isInactive
            ? "bg-slate-100 text-slate-400"
            : "bg-emerald-50 text-emerald-600",
        )}
      >
        <BadgeCheck className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
          <span className="truncate">{cert.name}</span>
          {cert.source === "CMARKET" && (
            <span className="shrink-0 rounded bg-primary/10 px-1 py-px text-[10px] font-semibold text-primary-text">
              씨마켓
            </span>
          )}
        </p>
        <p className="truncate text-2xs text-slate-500">
          {[cert.issuer, expiresLabel ? `${expiresLabel}까지` : null]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      {statusChip && (
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-2xs font-semibold",
            statusChip.className,
          )}
        >
          {statusChip.label}
        </span>
      )}
    </li>
  );
}

function ProfileSkeleton() {
  return (
    <div aria-busy className="flex flex-col">
      <Skeleton className="h-40 w-full rounded-none" />
      <div className="relative flex items-start gap-3.5 px-5">
        <Skeleton className="-mt-9 h-[72px] w-[72px] shrink-0 rounded-2xl border-[3px] border-white" />
        <div className="flex flex-1 flex-col gap-2 pt-3">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 px-5 pt-4">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-[72px] rounded-xl" />
        ))}
      </div>
      <div className="flex flex-col gap-3 px-5 py-6">
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    </div>
  );
}

function ProfileLoadError() {
  return (
    <div className="flex flex-col items-center gap-2 px-6 pb-12 pt-16 text-center">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
        <Building2 className="h-5 w-5" />
      </span>
      <p className="text-sm font-semibold text-slate-700">
        기업 프로필을 불러오지 못했어요
      </p>
      <p className="text-xs text-slate-500">잠시 후 다시 열어 주세요</p>
    </div>
  );
}

function ProfileBody({
  profile,
  displayName,
}: {
  profile: CompanyProfileData;
  displayName: string;
}) {
  const [introExpanded, setIntroExpanded] = useState(false);

  const { reviewSummary } = profile;
  const hasReviews = reviewSummary.totalReviews > 0;
  const sentimentTotal =
    reviewSummary.positiveReviewCount + reviewSummary.negativeReviewCount;
  const positiveRate =
    sentimentTotal > 0
      ? Math.round((reviewSummary.positiveReviewCount / sentimentTotal) * 100)
      : null;
  // 신뢰도는 점수(몇 점)를 보이지 않고 «상위 N%» 만 보인다(대표 결정 2026-09-17).
  // 퍼센타일이 없으면(하위 구간·집계 전) 점수로 대신하지 않는다.
  const percentileLabel = profile.trustPercentile
    ? `상위 ${formatTrustPercentile(profile.trustPercentile)}%`
    : null;
  const establishedYear = profile.establishedAt?.slice(0, 4) ?? null;
  const identityLine = [
    profile.ceoName ? `대표 ${profile.ceoName}` : null,
    profile.region,
  ]
    .filter(Boolean)
    .join(" · ");
  const productTags = splitProductDescription(profile.productDescription);
  const intro = profile.companyIntro;
  const introIsLong =
    !!intro && (intro.length > 110 || intro.split("\n").length > 3);
  const certificationTotal =
    profile.certifications.length || profile.certificationCount;
  // 동기화가 늦어 만료가 지난 분야가 남아 있을 수 있다 — 날짜로 한 번 더 거른다
  const procurementCategories = (profile.procurementCategories ?? []).filter(
    (category) => getCertExpiryState(category.validUntil) !== "expired",
  );
  const transactions = profile.recentTransactions ?? null;
  const maxInstitutionContracts = Math.max(
    1,
    ...(transactions?.topInstitutions.map((i) => i.contractCount) ?? []),
  );

  const infoRows: { label: string; value: ReactNode }[] = [];
  if (profile.establishedAt) {
    infoRows.push({
      label: "설립일",
      value: profile.establishedAt.slice(0, 10).replace(/-/g, "."),
    });
  }
  if (profile.homepageUrl) {
    infoRows.push({
      label: "홈페이지",
      value: (
        <a
          href={profile.homepageUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex max-w-full items-center gap-1 text-primary-text hover:underline"
        >
          <span className="truncate">{toDisplayHost(profile.homepageUrl)}</span>
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
      ),
    });
  }
  if (profile.joinedAt) {
    infoRows.push({ label: "세모 가입", value: formatYearMonth(profile.joinedAt) });
  }

  return (
    <>
      {/* 히어로 — 랜딩 썸네일, 없으면 이니셜 그라데이션 */}
      <ProfileHero
        imageUrl={profile.landingThumbnailUrl}
        name={displayName}
        className="h-40"
        showInitial={profile.isRevealed}
      >
        {profile.homepageUrl && (
          <a
            href={profile.homepageUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="absolute left-3 top-3 inline-flex max-w-[70%] items-center gap-1 rounded-full bg-black/40 px-2.5 py-1 text-2xs font-semibold text-white backdrop-blur-sm transition-colors hover:bg-black/60"
          >
            <Globe className="h-3 w-3 shrink-0" />
            <span className="truncate">
              {toDisplayHost(profile.homepageUrl)}
            </span>
            <ExternalLink className="h-3 w-3 shrink-0" />
          </a>
        )}
        {!profile.isRevealed && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-950/25 text-white">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/30 backdrop-blur-sm">
              <Lock className="h-4 w-4" />
            </span>
            <span className="text-xs font-semibold">
              계약 전에는 회사 정보가 가려져요
            </span>
          </div>
        )}
      </ProfileHero>

      {/* 신원 — 로고만 히어로에 겹치고 회사명은 온전히 아래에 둔다 (가독성) */}
      {/* relative — 히어로(position:relative)보다 위에 칠해져야 겹친 로고가 안 가려진다 */}
      <div className="relative flex items-start gap-3.5 px-5">
        <div className="-mt-9 h-[72px] w-[72px] shrink-0 overflow-hidden rounded-2xl border-[3px] border-white bg-white shadow-md ring-1 ring-slate-900/5">
          {profile.isRevealed || profile.logoUrl ? (
            <VendorAvatar
              name={toAvatarName(displayName)}
              imageUrl={profile.logoUrl}
              className="h-full w-full rounded-none border-0 text-2xl"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-slate-100 text-slate-400">
              <Lock className="h-6 w-6" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 pt-2.5">
          <h2 className="truncate text-lg font-bold leading-tight text-slate-900">
            {displayName}
          </h2>
          <p className="mt-1 truncate text-xs text-slate-500">
            {profile.isRevealed
              ? identityLine || " "
              : "회사명·대표자·연락처는 계약 후 공개돼요"}
          </p>
          <LastActiveBadge
            lastActiveAt={profile.lastActiveAt}
            className="mt-1"
          />
        </div>
      </div>

      {/* 한눈에 — 신뢰도·후기·업력 */}
      <div className="grid grid-cols-3 gap-2 px-5 pt-4">
        <StatTile label="신뢰도" value={percentileLabel ?? "집계 전"} />
        <StatTile
          label="거래 후기"
          value={
            hasReviews ? (
              <>
                <Star className="h-4 w-4 shrink-0 fill-amber-400 text-amber-400" />
                {reviewSummary.averageRating.toFixed(1)}
              </>
            ) : (
              "—"
            )
          }
          sub={hasReviews ? `${reviewSummary.totalReviews}건` : "아직 없음"}
        />
        <StatTile
          label="업력"
          value={
            profile.yearsInBusiness != null
              ? `${profile.yearsInBusiness}년`
              : "—"
          }
          sub={establishedYear ? `${establishedYear}년 설립` : undefined}
        />
      </div>

      {/* 의무구매 대상 인증 — 공공기관 발주자에게 가장 결정적인 정보라 타일 바로 아래 */}
      {procurementCategories.length > 0 && (
        <div className="mx-5 mt-3 rounded-xl border border-emerald-100 bg-emerald-50/70 px-3.5 py-3">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800">
            <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" />
            공공기관 의무구매 대상 인증을 보유하고 있어요
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {procurementCategories.map((category) => (
              <span
                key={category.code}
                className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200/70"
              >
                {category.label}
                {getCertExpiryState(category.validUntil) === "soon" && (
                  <span className="text-2xs font-medium text-amber-600">
                    곧 만료
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6 px-5 pb-6 pt-6">
        {/* 거래 실적 */}
        {transactions && (
          <Section
            title="거래 실적"
            aside={
              <span className="text-xs text-slate-400">
                최근 {transactions.years}년
              </span>
            }
          >
            {transactions.contractCount > 0 ? (
              <div className="flex flex-col gap-3 rounded-xl border border-slate-100 p-3.5">
                <div className="flex items-end gap-6">
                  <div>
                    <p className="text-2xs text-slate-500">계약</p>
                    <p className="text-2xl font-bold leading-tight tabular-nums text-slate-900">
                      {transactions.contractCount}
                      <span className="ml-0.5 text-sm font-medium text-slate-500">
                        건
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-2xs text-slate-500">거래기관</p>
                    <p className="text-2xl font-bold leading-tight tabular-nums text-slate-900">
                      {transactions.institutionCount}
                      <span className="ml-0.5 text-sm font-medium text-slate-500">
                        곳
                      </span>
                    </p>
                  </div>
                </div>
                {transactions.topInstitutions.length > 0 ? (
                  <ul className="flex flex-col gap-2 border-t border-slate-100 pt-3">
                    {transactions.topInstitutions.map((institution) => (
                      <li
                        key={institution.name}
                        className="flex items-center gap-2 text-sm"
                      >
                        <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span className="min-w-0 flex-1 truncate text-slate-700">
                          {institution.name}
                        </span>
                        <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-slate-100">
                          <span
                            className="block h-full rounded-full bg-primary/70"
                            style={{
                              width: `${(institution.contractCount / maxInstitutionContracts) * 100}%`,
                            }}
                          />
                        </span>
                        <span className="w-9 shrink-0 text-right text-xs tabular-nums text-slate-500">
                          {institution.contractCount}건
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : !profile.isRevealed ? (
                  <p className="flex items-center gap-1 border-t border-slate-100 pt-2.5 text-2xs text-slate-400">
                    <Lock className="h-3 w-3" />
                    거래기관 이름은 계약 후 공개돼요
                  </p>
                ) : null}
              </div>
            ) : (
              <EmptyNote
                icon={<Building2 className="h-4 w-4" />}
                title={`최근 ${transactions.years}년 거래 실적이 아직 없어요`}
                description="계약이 성립하면 거래기관과 건수가 여기에 쌓여요"
              />
            )}
          </Section>
        )}

        {/* 거래 후기 */}
        <Section
          title="거래 후기"
          aside={
            hasReviews ? (
              <span className="text-xs text-slate-400">
                {reviewSummary.totalReviews}건
              </span>
            ) : undefined
          }
        >
          {hasReviews ? (
            <div className="flex flex-col gap-3 rounded-xl border border-slate-100 p-3.5">
              <div className="flex items-center gap-3">
                <span className="text-3xl font-bold leading-none tabular-nums text-slate-900">
                  {reviewSummary.averageRating.toFixed(1)}
                </span>
                <div className="flex flex-col gap-1">
                  <RatingStars rating={reviewSummary.averageRating} />
                  <span className="text-2xs text-slate-500">5점 만점</span>
                </div>
                {positiveRate != null && (
                  <div className="ml-auto flex w-28 flex-col gap-1">
                    <div className="flex justify-between text-2xs">
                      <span className="font-semibold text-emerald-600">
                        긍정 {positiveRate}%
                      </span>
                      <span className="text-slate-400">
                        부정 {reviewSummary.negativeReviewCount}
                      </span>
                    </div>
                    <div className="flex h-1.5 overflow-hidden rounded-full bg-rose-200">
                      <div
                        className="h-full rounded-full bg-emerald-500"
                        style={{ width: `${positiveRate}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
              {reviewSummary.topTags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
                  {reviewSummary.topTags.map((tag) => (
                    <span
                      key={tag.label}
                      className="inline-flex items-center gap-1 rounded-full bg-primary/5 px-2.5 py-1 text-xs text-slate-700"
                    >
                      <ThumbsUp className="h-3 w-3 text-primary-text" />
                      {tag.label}
                      <span className="font-semibold tabular-nums text-primary-text">
                        {tag.count}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <EmptyNote
              icon={<MessageSquare className="h-4 w-4" />}
              title="아직 거래 후기가 없어요"
              description="거래를 마친 발주기관의 후기가 여기에 쌓여요"
            />
          )}
        </Section>

        {/* 보유 인증 */}
        {certificationTotal > 0 && (
          <Section
            title="보유 인증"
            aside={
              <span className="text-xs text-slate-400">
                {certificationTotal}건
              </span>
            }
          >
            {profile.certifications.length > 0 ? (
              <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-100">
                {profile.certifications.map((cert) => (
                  <CertificationRow key={cert.id} cert={cert} />
                ))}
              </ul>
            ) : (
              <EmptyNote
                icon={<Lock className="h-4 w-4" />}
                title={`검증된 인증서 ${profile.certificationCount}건 보유`}
                description="인증서 이름과 유효기간은 계약 후 공개돼요"
              />
            )}
          </Section>
        )}

        {/* 회사 소개 */}
        {(intro || productTags.length > 0) && (
          <Section title="회사 소개">
            {intro && (
              <div>
                <p
                  className={cn(
                    "whitespace-pre-wrap text-sm leading-relaxed text-slate-700",
                    introIsLong && !introExpanded && "line-clamp-4",
                  )}
                >
                  {intro}
                </p>
                {introIsLong && (
                  <button
                    type="button"
                    onClick={() => setIntroExpanded((expanded) => !expanded)}
                    className="mt-1 text-xs font-semibold text-slate-500 hover:text-slate-800"
                  >
                    {introExpanded ? "접기" : "더보기"}
                  </button>
                )}
              </div>
            )}
            {productTags.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <span className="text-2xs font-medium text-slate-500">
                  취급 품목
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {productTags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* 기본 정보 */}
        {infoRows.length > 0 && (
          <Section title="기본 정보">
            <dl className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
              {infoRows.map((row) => (
                <Fragment key={row.label}>
                  <dt className="text-slate-400">{row.label}</dt>
                  <dd className="min-w-0 truncate text-slate-700">
                    {row.value}
                  </dd>
                </Fragment>
              ))}
            </dl>
          </Section>
        )}
      </div>
    </>
  );
}

export interface CompanyProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: CompanyProfileData | null;
  isLoading?: boolean;
  /** 조회 실패 — 넘기지 않으면 profile 이 비어 있는 동안 스켈레톤을 유지한다 */
  isError?: boolean;
  /** 익명 단계에서 카드가 이미 보여주던 익명 표기 (예: "익명 공급사") */
  anonymousLabel?: string;
  /** 채팅 버튼 — 넘기면 하단에 고정으로 그려진다 */
  onChatClick?: () => void;
}

/**
 * 기업 프로필 시트 (풀/익명 공용).
 *
 * isRevealed=false 면 백엔드가 신원 필드를 null 로 내리므로,
 * 이 컴포넌트는 내려온 것만 그리면 익명 변형이 저절로 된다 —
 * 신뢰도·후기·업력·인증 개수는 익명에도 채워져 있다.
 */
export function CompanyProfileDialog({
  open,
  onOpenChange,
  profile,
  isLoading,
  isError,
  anonymousLabel,
  onChatClick,
}: CompanyProfileDialogProps) {
  const displayName = profile?.companyName ?? anonymousLabel ?? "공급사";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="flex max-h-[88vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[440px]"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>기업 프로필</DialogTitle>
          <DialogDescription>{displayName}의 기업 프로필</DialogDescription>
        </DialogHeader>

        {/* 기본 닫기 버튼은 어두운 히어로 위에서 안 보여 반투명 원형으로 따로 둔다 */}
        <DialogClose className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm transition-colors hover:bg-black/55 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80">
          <XIcon className="h-4 w-4" />
          <span className="sr-only">닫기</span>
        </DialogClose>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading || (!profile && !isError) ? (
            <ProfileSkeleton />
          ) : profile ? (
            <ProfileBody
              key={profile.supplierId}
              profile={profile}
              displayName={displayName}
            />
          ) : (
            <ProfileLoadError />
          )}
        </div>

        {onChatClick && profile && !isLoading && (
          <div className="shrink-0 border-t border-slate-100 bg-white p-4">
            <button
              type="button"
              onClick={onChatClick}
              className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[#1250d8]"
            >
              <MessageSquare className="h-4 w-4" />
              1:1 채팅하기
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
